import { removeQualifying } from "@/utils/questions";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ActionIcon, Flex, NumberInput, Select, Text } from "@mantine/core";

import { AddConditionButton } from "./add-condition-button";
import {
  NumericalRule,
  type QuestionStateSetter,
  QuestionType,
  type ReviewPageDraftQuestion,
} from "@/types/screener-builder";
import { parseNumericalRule } from "@/utils/questions";

export const numberRulesValid = (
  question: ReviewPageDraftQuestion<QuestionType.Number>,
): boolean => {
  if (!question.isQualifying) {
    return true;
  }

  return !!question.validationRules?.numericalRule;
};

interface NumberValidationRulesProps {
  question: ReviewPageDraftQuestion<QuestionType.Number>;
  setQuestion: QuestionStateSetter<QuestionType.Number>;
}

export const NumberValidationRules = ({
  question,
  setQuestion,
}: NumberValidationRulesProps) => {
  const numericalRule = question.validationRules?.numericalRule;
  const parsedRule = parseNumericalRule(numericalRule);

  const handleAddCondition = () => {
    setQuestion({
      ...question,
      isQualifying: true,
      validationRules: {
        ...question.validationRules,
        numericalRule: "=0",
      },
    });
  };

  if (!question.isQualifying || !parsedRule) {
    return <AddConditionButton onClick={handleAddCondition} />;
  }

  return (
    <Flex align="center" gap="xs">
      <Text size="sm" fw={400} w="1/3">
        Answer is
      </Text>
      <Select
        className="w-1/3"
        data={[
          { value: NumericalRule.EqualTo, label: "Equal to" },
          { value: NumericalRule.LessThan, label: "Less than" },
          { value: NumericalRule.GreaterThan, label: "Greater than" },
          { value: NumericalRule.NotEqualTo, label: "Not equal to" },
          {
            value: NumericalRule.GreaterThanOrEqualTo,
            label: "Greater than or equal to",
          },
          {
            value: NumericalRule.LessThanOrEqualTo,
            label: "Less than or equal to",
          },
        ]}
        value={parsedRule.condition}
        onChange={(condition) => {
          if (!condition) {
            return;
          }

          setQuestion({
            ...question,
            validationRules: {
              ...question.validationRules,
              numericalRule: `${condition}${
                parseNumericalRule(question.validationRules?.numericalRule)
                  ?.value ?? "0"
              }`,
            },
          });
        }}
      />
      <NumberInput
        className="w-1/3 flex-grow"
        onChange={(value) => {
          setQuestion({
            ...question,
            validationRules: {
              ...question.validationRules,
              numericalRule: `${
                parseNumericalRule(question.validationRules?.numericalRule)
                  ?.condition ?? NumericalRule.EqualTo
              }${value || 0}`,
            },
          });
        }}
        placeholder="0"
        value={parsedRule.value ?? "0"}
      />
      <ActionIcon
        variant="transparent"
        size="xs"
        onClick={() => setQuestion(removeQualifying(question))}
      >
        <FontAwesomeIcon icon={faXmark} />
      </ActionIcon>
    </Flex>
  );
};
